import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { DataTable, Column } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatTile } from '@/components/StatTile';
import { CampaignStatusPill } from '@/components/facebook/CampaignStatusPill';
import { facebookAdSetsService, FbAdSet, getAdSetInsights, getAdSetPlacements } from '@/services/facebook-ad-sets.service';
import { facebookAdsService, FbAd } from '@/services/facebook-ads.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { formatMetaInsightsValue, formatMetaCount, formatMetaPercent, formatMetaCurrency } from '@/utils/currency';
import { RefreshCw, Plus, BarChart2, Settings2, Users, Layers } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AdSetDetail() {
  const { adSetId } = useParams<{ adSetId: string }>();
  const nav = useNavigate();
  const [adSet, setAdSet] = useState<FbAdSet | null>(null);
  const [ads, setAds] = useState<FbAd[] | null>(null);
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async (force = false) => {
    setRefreshing(true);
    try {
      await Promise.all([
        facebookAdSetsService.get(adSetId!, force).then(setAdSet),
        facebookAdsService.byAdSet(adSetId!, force).then((r) => setAds(r.data)),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adSetId]);

  const doPause = async () => {
    setBusy(true);
    try {
      await facebookAdSetsService.pause(adSetId!);
      toast.success('Paused');
      await refresh(true);
    } finally {
      setBusy(false);
    }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      await facebookAdSetsService.resume(adSetId!);
      toast.success('Resumed');
      await refresh(true);
    } finally {
      setBusy(false);
    }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await facebookAdSetsService.archive(adSetId!);
      toast.success('Archived');
      setConfirmArchive(false);
      await refresh(true);
    } finally {
      setBusy(false);
    }
  };

  // Meta reports the account currency; until it does, show '—' rather than guess.
  const currency = account?.currency ?? '';
  const insights = adSet ? getAdSetInsights(adSet) : null;

  const effectiveStatus = adSet ? (adSet.effective_status ?? adSet.status) : '';
  const isPaused = effectiveStatus === 'PAUSED' || effectiveStatus === 'ADSET_PAUSED';
  const isActive = effectiveStatus === 'ACTIVE';

  const adCols: Column<FbAd>[] = [
    {
      key: 'name',
      header: 'Ad',
      width: '2fr',
      render: (a) => (
        <div>
          <div className="font-semibold text-text-primary">{a.name}</div>
          <div className="font-mono text-xs text-text-muted">{a.id}</div>
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (a) => <CampaignStatusPill status={a.effective_status ?? a.status} /> },
    { key: 'imp', header: 'Impr.', render: (a) => <span className="tabular-nums">{formatMetaCount(a.insights?.data?.[0]?.impressions)}</span> },
    { key: 'clicks', header: 'Clicks', render: (a) => <span className="tabular-nums">{formatMetaCount(a.insights?.data?.[0]?.clicks)}</span> },
    { key: 'spend', header: 'Spend', render: (a) => <span className="tabular-nums">{formatMetaInsightsValue(a.insights?.data?.[0]?.spend, currency)}</span> },
    {
      key: 'act',
      header: '',
      width: '180px',
      render: (a) => (
        <div className="flex gap-1">
          {(a.status === 'PAUSED' || a.effective_status === 'PAUSED') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await facebookAdsService.resume(a.id, adSetId);
                toast.success('Resumed');
                refresh(true);
              }}
            >
              Resume
            </Button>
          )}
          {(a.status === 'ACTIVE' || a.effective_status === 'ACTIVE') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await facebookAdsService.pause(a.id, adSetId);
                toast.success('Paused');
                refresh(true);
              }}
            >
              Pause
            </Button>
          )}
          {a.status !== 'ARCHIVED' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await facebookAdsService.archive(a.id, adSetId);
                toast.success('Archived');
                refresh(true);
              }}
            >
              Archive
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!adSet) {
    return (
      <>
        <Topbar title="Ad Set" breadcrumb="Facebook Ads" />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-28 rounded-2xl" />
          <div className="grid grid-cols-4 gap-3">
            {[...Array(7)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={adSet.name}
        breadcrumb={`Facebook Ads / Ad Set / ${adSet.name}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <CampaignStatusPill status={effectiveStatus} />
            {isPaused && (
              <Button size="sm" loading={busy} onClick={doResume}>
                Resume
              </Button>
            )}
            {isActive && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={doPause}>
                Pause
              </Button>
            )}
            {adSet.status !== 'ARCHIVED' && (
              <Button size="sm" variant="danger" disabled={busy} onClick={() => setConfirmArchive(true)}>
                Archive
              </Button>
            )}
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
              onClick={() => refresh(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <Link to={`/facebook/campaigns/${adSet.campaign_id}`}>
              <Button size="sm" variant="secondary">
                Back to campaign
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-5">
        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Impressions" value={formatMetaCount(insights?.impressions)} accent="blue" icon={<BarChart2 size={16} />} />
          <StatTile label="Clicks" value={formatMetaCount(insights?.clicks)} accent="green" />
          <StatTile label="Spend" value={formatMetaInsightsValue(insights?.spend, currency)} accent="gold" />
          <StatTile label="Reach" value={formatMetaCount(insights?.reach)} accent="amber" />
          <StatTile label="CTR" value={formatMetaPercent(insights?.ctr)} accent="green" />
          <StatTile label="CPC" value={formatMetaInsightsValue(insights?.cpc, currency)} accent="gold" />
          <StatTile label="CPM" value={formatMetaInsightsValue(insights?.cpm, currency)} accent="blue" />
        </div>

        {/* Configuration */}
        <Card>
          <SectionHeader title="Configuration" icon={<Settings2 size={16} />} />
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <div className="text-text-muted">Billing event</div>
              <div className="mt-0.5 font-semibold text-text-primary">{adSet.billing_event ?? '—'}</div>
            </div>
            <div>
              <div className="text-text-muted">Optimization goal</div>
              <div className="mt-0.5 font-semibold text-text-primary">{adSet.optimization_goal ?? '—'}</div>
            </div>
            <div>
              <div className="text-text-muted">Daily budget</div>
              <div className="mt-0.5 tabular-nums font-semibold text-text-primary">
                {adSet.daily_budget ? formatMetaCurrency(adSet.daily_budget, currency) : '—'}
              </div>
            </div>
            <div>
              <div className="text-text-muted">Lifetime budget</div>
              <div className="mt-0.5 tabular-nums font-semibold text-text-primary">
                {adSet.lifetime_budget ? formatMetaCurrency(adSet.lifetime_budget, currency) : '—'}
              </div>
            </div>
            <div>
              <div className="text-text-muted">Started</div>
              <div className="mt-0.5 font-semibold text-text-primary">
                {adSet.start_time ? new Date(adSet.start_time).toLocaleString() : '—'}
              </div>
            </div>
            <div>
              <div className="text-text-muted">Ends</div>
              <div className="mt-0.5 font-semibold text-text-primary">
                {adSet.end_time ? new Date(adSet.end_time).toLocaleString() : 'No end'}
              </div>
            </div>
          </div>
        </Card>

        {/* Targeting */}
        {adSet.targeting && (
          <Card>
            <SectionHeader title="Targeting" icon={<Users size={16} />} />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <div className="text-text-muted">Countries</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(adSet.targeting.geo_locations?.countries ?? ['—']).map((c) => (
                    <span key={c} className="rounded-full border border-border bg-surface-alt px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-text-muted">Age range</div>
                <div className="mt-0.5 tabular-nums font-semibold text-text-primary">
                  {adSet.targeting.age_min ?? '?'} – {adSet.targeting.age_max ?? '?'}
                </div>
              </div>
              <div>
                <div className="text-text-muted">Genders</div>
                <div className="mt-0.5 font-semibold text-text-primary">
                  {!adSet.targeting.genders || adSet.targeting.genders.length === 0
                    ? 'All'
                    : adSet.targeting.genders.map((g) => (g === 1 ? 'Male' : 'Female')).join(', ')}
                </div>
              </div>
              {(() => {
                const pl = getAdSetPlacements(adSet);
                const label = pl.isAutomatic
                  ? 'Automatic (all eligible surfaces)'
                  : [
                      pl.facebook && 'Facebook',
                      pl.instagram && 'Instagram',
                      pl.messenger && 'Messenger',
                      pl.audienceNetwork && 'Audience Network',
                    ].filter(Boolean).join(' + ') || 'None';
                return (
                  <div className="col-span-2">
                    <div className="text-text-muted">Placements</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-text-primary">{label}</span>
                      {pl.isAutomatic && (
                        <span className="rounded-full bg-info-soft px-2 py-0.5 text-2xs font-semibold text-info">Auto</span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Card>
        )}

        {/* Ads table */}
        <Card padded={false} className="overflow-hidden">
          <SectionHeader
            title="Ads"
            subtitle={ads ? `${ads.length} ad${ads.length === 1 ? '' : 's'}` : undefined}
            icon={<Layers size={16} />}
            action={
              <Button
                size="sm"
                iconLeft={<Plus size={13} />}
                onClick={() => nav(`/facebook/ad-sets/${adSetId}/ads/new`)}
              >
                New Ad
              </Button>
            }
            className="px-5 pt-5 pb-0 mb-0"
          />
          <div className="mt-4">
            {!ads ? (
              <div className="space-y-2 px-5 pb-5">
                {[...Array(2)].map((_, i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
              </div>
            ) : (
              <DataTable
                rows={ads}
                columns={adCols}
                empty="No ads yet — click + New Ad to create one. The ad set won't deliver until at least one ad is added."
              />
            )}
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmArchive}
        title="Archive ad set?"
        message={`Archive "${adSet.name}"? This stops the ad set permanently.`}
        danger
        onConfirm={doArchive}
        onCancel={() => setConfirmArchive(false)}
      />
    </>
  );
}
